/**
 * The MIDI backend: ports → parser → `InputEvent`s into the router.
 *
 * A message from any connected port the user has not turned off is offered to every binding on its control, on its own channel and on omni; no binding names a device.
 */
import { markActivity } from '~/lib/sessionActivity'
import { connectedInputs, connectedOutputs, requestMidi } from './access'
import { describePorts, matchOutput, midiKey } from './devices'
import { toInputEvent } from './events'
import { createParser } from './parser'

const channelKey = ch => (ch === 'omni' ? '*' : ch)

// A message heard on an input this soon after it went out to its paired output is the port echoing (loopMIDI, rtpMIDI), not a hand.
export const SENT_ECHO_MS = 300

/**
 * Input port id → output port id. `matchOutput` first; an input left over takes a same-maker output left over, in order (Korg names its halves differently).
 */
export const pairPorts = (inputs, outputs) => {
	const pairs = new Map()
	const taken = new Set()
	for (const d of inputs) {
		const o = matchOutput(d, outputs)
		if (o && !taken.has(o.portId)) {
			pairs.set(d.portId, o.portId)
			taken.add(o.portId)
		}
	}
	for (const d of inputs) {
		if (pairs.has(d.portId) || !d.manufacturer) continue
		const o = outputs.find(o => !taken.has(o.portId) && o.manufacturer === d.manufacturer)
		if (o) {
			pairs.set(d.portId, o.portId)
			taken.add(o.portId)
		}
	}
	return pairs
}

/**
 * @param onPorts   the described inputs, on every port change
 * @param onPlayed  an input sent its first message since it connected: its feedback starts now
 */
export const createMidiBackend = ({ router, onPorts, onPlayed, now = () => performance.now() }) => {
	let access = null
	let described = []
	let outputs = []
	const outputPorts = new Map()
	let pairs = new Map()
	// Input port ids that sent since they connected; a port that never played never hears feedback, so a loopback cannot start a loop.
	const played = new Set()
	// The output ports feedback goes to, rebuilt when the pairs, `played` or `disabled` change.
	let fbPorts = null
	// output port id → (message bytes → time sent), for the echo filter.
	const sent = new Map()
	// `${channel|*}|${msb cc}` of every 14-bit binding: the parser pairs only these.
	let fourteen = new Set()
	// `deviceKey`s the user turned off.
	let disabled = new Set()
	const attached = new Map()
	const taps = new Set()

	const receive = (port, msg) => {
		const device = described.find(d => d.portId === port.id)
		const ignored = !!device && disabled.has(device.key)
		for (const fn of taps) fn({ device, msg, ignored })
		if (!played.has(port.id)) {
			played.add(port.id)
			fbPorts = null
			onPlayed?.()
		}
		if (ignored) return
		for (const ch of [msg.channel, 'omni']) {
			for (const b of router.bindingsFor(midiKey(ch, msg.type, msg.number))) {
				router.dispatch(b, toInputEvent(msg, b))
			}
		}
	}

	const attach = () => {
		const live = new Set()
		for (const port of connectedInputs(access)) {
			live.add(port.id)
			if (attached.has(port.id)) continue
			const parser = createParser({
				onMessage: msg => receive(port, msg),
				is14bit: (ch, cc) => fourteen.has(`${ch}|${cc}`) || fourteen.has(`*|${cc}`),
			})
			// Assigning the handler opens the port.
			port.onmidimessage = e => {
				if (isEcho(port.id, e.data)) return
				// Channel messages only: clock and active sensing (0xF0+) stream with nobody touching the controller. markActivity ignores it outside a guarded session and during the warning.
				if (e.data[0] < 0xf0) markActivity()
				parser.push(e.data)
			}
			attached.set(port.id, { port, parser })
		}
		for (const [id, { port, parser }] of attached) {
			if (live.has(id)) continue
			parser.reset()
			port.onmidimessage = null
			attached.delete(id)
			played.delete(id)
			fbPorts = null
		}
	}

	const refresh = () => {
		described = describePorts(connectedInputs(access))
		const ports = connectedOutputs(access)
		outputs = describePorts(ports)
		outputPorts.clear()
		for (const p of ports) outputPorts.set(p.id, p)
		pairs = pairPorts(described, outputs)
		for (const id of sent.keys()) if (!outputPorts.has(id)) sent.delete(id)
		fbPorts = null
		attach()
		onPorts?.(described)
	}

	/** The output ports feedback goes to: one per played, enabled input, each once. */
	const feedbackPorts = () => {
		if (fbPorts) return fbPorts
		fbPorts = new Map()
		for (const d of described) {
			if (!played.has(d.portId) || disabled.has(d.key)) continue
			const port = outputPorts.get(pairs.get(d.portId))
			if (port) fbPorts.set(port.id, port)
		}
		return fbPorts
	}

	const isEcho = (inputId, data) => {
		const log = sent.get(pairs.get(inputId))
		if (!log) return false
		const key = String([...data])
		const t = log.get(key)
		if (t == null) return false
		log.delete(key)
		return now() - t < SENT_ECHO_MS
	}

	const noteSent = (portId, msgs) => {
		const t = now()
		if (!sent.has(portId)) sent.set(portId, new Map())
		const log = sent.get(portId)
		for (const [key, at] of log) if (t - at >= SENT_ECHO_MS) log.delete(key)
		for (const m of msgs) log.set(String(m), t)
	}

	return {
		/** ⚠️ From a user action only — see `requestMidi`. */
		async start() {
			access = await requestMidi()
			access.onstatechange = refresh
			refresh()
		},
		stop() {
			if (access) access.onstatechange = null
			for (const { port, parser } of attached.values()) {
				parser.reset()
				port.onmidimessage = null
			}
			attached.clear()
			played.clear()
			sent.clear()
			fbPorts = null
			access = null
			described = []
			outputs = []
			outputPorts.clear()
			pairs = new Map()
		},
		/** Feedback to the outputs of played, enabled inputs; returns whether any went out. */
		send(msgs) {
			let any = false
			for (const port of feedbackPorts().values()) {
				try {
					for (const m of msgs) port.send(m)
					noteSent(port.id, msgs)
					any = true
				} catch {
					// Unplugged between `statechange`s; the next refresh drops it.
				}
			}
			return any
		},
		/** The store's bindings and disabled inputs. */
		configure({ bindings, disabledInputs }) {
			disabled = new Set(disabledInputs ?? [])
			fbPorts = null
			fourteen = new Set(
				(bindings ?? [])
					.filter(b => b.source?.backend === 'midi' && b.source.type === 'cc14')
					.map(b => `${channelKey(b.source.channel)}|${b.source.number}`)
			)
		},
		/** Every decoded message `{ device, msg, ignored }`, before routing — learn mode and the monitor listen here; `ignored`: its device is off. Returns the unsubscribe. */
		tap(fn) {
			taps.add(fn)
			return () => taps.delete(fn)
		},
	}
}
